import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const NotificationContext = createContext();

export const useNotificationContext = () => useContext(NotificationContext);

export const NotificationProvider = ({ children }) => {
  const { token, isAuthenticated, user } = useAuth();
  const [permission, setPermission] = useState('default');
  const [unreadCount, setUnreadCount] = useState(0);
  const [enabled, setEnabled] = useState(false);
  const previousUnreadRef = useRef(0);
  const audioContextRef = useRef(null);

  // Check initial permission state
  useEffect(() => {
    if ('Notification' in window) {
      setPermission(Notification.permission);
      setEnabled(Notification.permission === 'granted');
    }
  }, []);

  // Play notification sound using Web Audio API
  const playNotificationSound = useCallback(() => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      // Pleasant notification tone
      oscillator.frequency.value = 880; // A5 note
      oscillator.type = 'sine';
      
      gainNode.gain.setValueAtTime(0.2, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.2);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.2);
      
      // Second tone for "ding-dong" effect
      setTimeout(() => {
        const osc2 = audioContext.createOscillator();
        const gain2 = audioContext.createGain();
        osc2.connect(gain2);
        gain2.connect(audioContext.destination);
        osc2.frequency.value = 1100; // Higher note
        osc2.type = 'sine';
        gain2.gain.setValueAtTime(0.15, audioContext.currentTime);
        gain2.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.15);
        osc2.start(audioContext.currentTime);
        osc2.stop(audioContext.currentTime + 0.15);
      }, 150);
    } catch (e) {
      console.log('Could not play notification sound:', e);
    }
  }, []);

  // Request notification permission
  const requestPermission = useCallback(async () => {
    if (!('Notification' in window)) {
      console.log('Browser does not support notifications');
      return false;
    }

    try {
      const result = await Notification.requestPermission();
      setPermission(result);
      setEnabled(result === 'granted');
      return result === 'granted';
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      return false;
    }
  }, []);

  // Show browser notification
  const showNotification = useCallback((title, options = {}) => {
    if (permission !== 'granted' || !enabled) return null;

    // Don't show notification if page is focused
    if (document.hasFocus() && !options.force) {
      // Just play sound for in-app notification
      playNotificationSound();
      return null;
    }

    const notification = new Notification(title, {
      icon: '/logo192.png',
      badge: '/logo192.png',
      tag: 'mealhoster-message',
      renotify: true,
      silent: false,
      ...options,
    });

    playNotificationSound();

    notification.onclick = () => {
      window.focus();
      notification.close();
      if (options.onClick) options.onClick();
    };

    return notification;
  }, [permission, enabled, playNotificationSound]);

  // Fetch unread count
  const fetchUnreadCount = useCallback(async () => {
    if (!isAuthenticated || !token) {
      setUnreadCount(0);
      return 0;
    }

    try {
      const response = await axios.get(`${API_URL}/api/messages/unread-count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const count = response.data.unread_count || 0;
      setUnreadCount(count);
      return count;
    } catch (error) {
      console.error('Error fetching unread count:', error);
      return 0;
    }
  }, [isAuthenticated, token]);

  // Poll for new messages
  useEffect(() => {
    if (!isAuthenticated || !token) {
      setUnreadCount(0);
      previousUnreadRef.current = 0;
      return;
    }

    const checkForNewMessages = async () => {
      const newCount = await fetchUnreadCount();
      
      // If we have new unread messages, show notification
      if (newCount > previousUnreadRef.current && enabled) {
        const newMessages = newCount - previousUnreadRef.current;
        showNotification('MealHoster', {
          body: `You have ${newMessages} new message${newMessages > 1 ? 's' : ''} 🍽️`,
          onClick: () => {
            window.location.href = '/messages';
          },
        });
      }
      
      previousUnreadRef.current = newCount;
    };

    // Initial check after a small delay to allow auth to settle
    const initialTimeout = setTimeout(checkForNewMessages, 1000);

    // Poll every 30 seconds
    const interval = setInterval(checkForNewMessages, 30000);

    return () => {
      clearTimeout(initialTimeout);
      clearInterval(interval);
    };
  }, [isAuthenticated, token, enabled, fetchUnreadCount, showNotification]);

  // Toggle notifications
  const toggleNotifications = useCallback(async () => {
    if (enabled) {
      setEnabled(false);
    } else {
      const granted = await requestPermission();
      if (granted) {
        playNotificationSound(); // Test sound
      }
    }
  }, [enabled, requestPermission, playNotificationSound]);

  // Mark messages as read (reduces count)
  const markAsRead = useCallback((count = 1) => {
    setUnreadCount(prev => Math.max(0, prev - count));
    previousUnreadRef.current = Math.max(0, previousUnreadRef.current - count);
  }, []);

  // Refresh unread count (called when viewing messages)
  const refreshUnreadCount = useCallback(() => {
    fetchUnreadCount();
  }, [fetchUnreadCount]);

  return (
    <NotificationContext.Provider value={{
      permission,
      unreadCount,
      enabled,
      requestPermission,
      showNotification,
      toggleNotifications,
      fetchUnreadCount,
      markAsRead,
      refreshUnreadCount,
      playNotificationSound,
    }}>
      {children}
    </NotificationContext.Provider>
  );
};

export default NotificationContext;
